import React, { useEffect, useRef } from 'react';
import { CheckIcon, LockIcon } from 'lucide-react';
import { bonuses, offer, price } from '../data/content';
import { trackPixel } from '../utils/metaPixel';
import { CtaButton } from './CtaButton';
import { ImageSlot } from './ImageSlot';
import { SectionHeading } from './SectionHeading';
type OfferSectionProps = {
  offerMockupUrl: string;
};

/** One row of the value stack: label on the left, value on the right (same values as the checkout) */
function StackRow({
  label,
  value,
  strong
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return <li className="flex items-baseline justify-between gap-4 py-2.5" data-id="element-199" data-mp="re678jg3pw09">
      <span className={`text-[15px] leading-snug ${strong ? 'font-semibold text-ink' : 'text-ink/80'}`} data-id="element-200" data-mp="rbtpo7e99nnq">{label}</span>
      <span className="shrink-0 font-display text-[16px] font-extrabold tabular-nums text-ink" data-id="element-201" data-mp="2t59jfd5n3imx">{value}</span>
    </li>;
}
export function OfferSection({
  offerMockupUrl
}: OfferSectionProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const observer = new IntersectionObserver(entries => {
      if (!fired && entries.some(e => e.isIntersecting)) {
        fired = true;
        trackPixel('ViewContent');
        observer.disconnect();
      }
    }, {
      threshold: 0.25
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section ref={ref} id="offer" aria-labelledby="offer-title" className="scroll-mt-0 bg-brand py-12 md:py-20" data-id="element-202" data-mp="1gocnk9i4gmus">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-203" data-mp="1ck4uy18jmmra">
        <SectionHeading id="offer-title" tone="dark" eyebrow={offer.eyebrow} title={offer.title} data-id="element-204" data-mp="17mv6anszaf4i" />

        <div className="mx-auto mt-8 max-w-[820px] overflow-hidden rounded-2xl bg-white md:mt-12" data-id="element-205" data-mp="172er7acvz7n">
          {/* The mockup has a pure white background, so this area stays white */}
          <div className="aspect-[4/3] bg-white px-2 pt-3 sm:aspect-[16/10] sm:px-6 sm:pt-6" data-id="element-206" data-mp="3ttzqy5mb28mm">
            <ImageSlot url={offerMockupUrl} alt={offer.mockupAlt} slotName="offerMockupUrl" fit="contain" data-id="element-207" data-mp="2nollxa9ilph" />
          </div>

          <div className="p-4 pb-6 sm:p-8 md:p-10" data-id="element-208" data-mp="3ktuma38hxoc5">
            <div className="text-center" data-id="element-209" data-mp="3kt1inb4u3ey8">
              <p className="font-display text-[12px] font-black tracking-[0.14em] text-coral-ink" data-id="element-210" data-mp="3oude3xae2uec">{offer.kicker}</p>
              <h3 className="mt-1 font-display text-[26px] font-black uppercase leading-tight tracking-tight text-brand md:text-[32px]" data-id="element-211" data-mp="3b2gqo6jo18oa">{offer.name}</h3>
              <p className="mx-auto mt-2 max-w-[560px] text-[16px] leading-relaxed text-ink/75" data-id="element-212" data-mp="3fw1h9dixj0dg">{offer.description}</p>
            </div>

            <div className="mt-8" data-id="element-213" data-mp="363ncebwk1dn2">
              <h4 className="font-display text-[13px] font-black tracking-[0.1em] text-brand" data-id="element-214" data-mp="364yhqg5netga">{offer.includedTitle}</h4>
              <ul className="mt-4 grid gap-3 md:grid-cols-2 md:gap-x-8" data-id="element-215" data-mp="2s70277flcqz7">
                {offer.included.map(item => <li key={item} className="flex gap-3 text-[16px] leading-snug text-ink" data-id="element-216" data-mp="2dhowpae3k6cp">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-leaf text-white" data-id="element-217" data-mp="28iu81rtg7nko">
                      <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-218" data-mp="2mivernwe9p82" />
                    </span>
                    {item}
                  </li>)}
              </ul>
            </div>

            {/* Value stack — exactly the checkout values: $159 + $155 in bonuses = $314 → today $27 */}
            <div className="mt-8 rounded-xl bg-sun-soft px-4 py-5 sm:px-8 sm:py-6" data-id="element-219" data-mp="2rh6rc6e2qdtz">
              <h4 className="text-center font-display text-[13px] font-black tracking-[0.12em] text-ink/80" data-id="element-220" data-mp="365kukifek4b2">{offer.stackTitle}</h4>
              <ul className="mt-3 divide-y divide-ink/10" data-id="element-221" data-mp="319ma35x736fx">
                <StackRow label={offer.mainItem.label} value={offer.mainItem.value} strong data-id="element-222" data-mp="3f8c7kmnxf84t" />
                {bonuses.items.map(b => <StackRow key={b.label} label={`${b.label.replace('BONUS', 'Bonus')} · ${b.short}`} value={b.value} data-id="element-223" data-mp="3a64oze8lctti" />)}
              </ul>
              <div className="mt-1 flex items-baseline justify-between gap-4 border-t-2 border-ink/20 pt-3" data-id="element-224" data-mp="1dhbs8k21yi1j">
                <span className="font-display text-[15px] font-black uppercase tracking-wide text-ink" data-id="element-225" data-mp="1ia4tgxybb88v">{offer.totalLabel}</span>
                <s className="font-display text-[22px] font-extrabold tabular-nums text-ink/55 decoration-coral decoration-[3px]" data-id="element-226" data-mp="1m9nhi2lfjzfx">{offer.totalValue}</s>
              </div>
              <div className="mt-4 text-center" data-id="element-227" data-mp="uk24iqpp2ieh">
                <p className="font-display text-[13px] font-black tracking-[0.16em] text-coral-ink" data-id="element-228" data-mp="ykuddiunmla8">{offer.todayLabel}</p>
                <p className="mt-1 font-display text-[4.6rem] font-black leading-none tracking-tight text-brand md:text-[5.6rem]" data-id="element-229" data-mp="13kgsjxu1mblo">{price}</p>
                <p className="mt-2 text-[14px] font-semibold text-ink/80" data-id="element-230" data-mp="29zhnq1b8upr7">{offer.priceNote}</p>
                <p className="mt-2 inline-block rounded-md bg-leaf-ink px-2.5 py-1 font-display text-[12px] font-black tracking-[0.06em] text-white" data-id="element-231" data-mp="2exj6sm8o6p2o">{offer.saveNote}</p>
              </div>
            </div>

            {/* The ONLY checkout button on the page */}
            <div className="mx-auto mt-6 max-w-[440px]" data-id="element-232" data-mp="2fcprtrzx89eo">
              <CtaButton action="checkout" align="center" data-id="element-233" data-mp="2fb4yu6t98yix" />
            </div>
            <div className="mt-4 text-center" data-id="element-234" data-mp="2bb4ksna2ipce">
              <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-ink/75" data-id="element-235" data-mp="26c9ul09rtyi9">
                <LockIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" data-id="element-236" data-mp="21cx66b394jdk" />
                {offer.ctaNote[0]}
              </p>
              <p className="mt-1 text-[13px] text-ink/65" data-id="element-237" data-mp="1wevn3q5tsk23">{offer.ctaNote[1]}</p>
              <p className="mt-1 text-[13px] font-semibold text-leaf-ink" data-id="element-238" data-mp="1sdtnh87zy5zl">{offer.guaranteeNote}</p>
            </div>

            <p className="mx-auto mt-5 max-w-[480px] text-center text-[13px] leading-relaxed text-ink/60" data-id="element-239" data-mp="3qruzzbitn05o">{offer.format}</p>
          </div>
        </div>
      </div>
    </section>;
}